import { useState, useEffect } from "react";
import { ChevronDown, Heart, SlidersHorizontal, Trash2 } from "lucide-react";
import services from "../../API";
import { useSelector } from "react-redux";
import { Loading } from "../Loader/Loading";
import ConfirmModal from "../Modals/ConfirmModal";
import { CircularLoader } from "../Loader/CircularLoader";

export default function LibraryTab({
  searchQuery,
  onSearchChange,
  selectedCustomCharacter,
  selectedTemplateCharacter,
  onSelectedCustomCharacterChange,
  onSelectedTemplateCharacterChange,
}) {
  const allCharacters = useSelector(
    (state) => state.data.apiData.characters?.data || []
  );
  const loading = useSelector((state) => state.data.loading.characters);


  const [viewFilter, setViewFilter] = useState("all");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [favoritedCharacters, setFavoritedCharacters] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    gender: [],
    ageCategory: [],
    type: [],
    status: [],
  });
  const [filterSectionsOpen, setFilterSectionsOpen] = useState({
    gender: true,
    ageCategory: true,
    type: false,
    status: false,
  });

  // Delete State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const toggleSelection = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  const handleDeleteAction = () => {
    setDeleteModalOpen(true);
  };

  // Extract unique filter options from API data
  const filterOptions = {
    gender: ["male", "female"],
    ageCategory: ["child", "young", "middle_aged", "old"],
    type: ["ai_generated", "template", "custom"],
    status: ["active", "inactive", "processing"],
  };

  const toggleFilter = (category, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter((v) => v !== value)
        : [...prev[category], value],
    }));
  };

  const toggleFilterSection = (section) => {
    setFilterSectionsOpen((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const clearFilters = () => {
    setSelectedFilters({
      gender: [],
      ageCategory: [],
      type: [],
      status: [],
    });
  };

  const toggleFavorite = (characterId, e) => {
    e.stopPropagation();
    setFavoritedCharacters((prev) => {
      const newFavorites = prev.includes(characterId)
        ? prev.filter((id) => id !== characterId)
        : [...prev, characterId];
      return newFavorites;
    });
  };



  const handleConfirmDelete = async () => {
    if (selectedItems.length === 0) return;

    setIsDeleting(true);
    try {
      await services.deleteMultipleCharacters(selectedItems);

      // Refresh list
      await getCharacters();

      // Clear selection if any deleted characters were selected for "Use"
      if (selectedItems.includes(selectedCustomCharacter)) {
        onSelectedCustomCharacterChange(null);
      }

      setDeleteModalOpen(false);
      clearSelection();
    } catch (error) {
      console.error("Error deleting characters:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCharacterClick = (character) => {
    const isCustom =
      character.type === "ai_generated" || character.type === "custom";
    const isSelected = isCustom
      ? selectedCustomCharacter === character._id
      : selectedTemplateCharacter === character._id;

    if (isCustom) {
      onSelectedCustomCharacterChange(isSelected ? null : character._id);
      onSelectedTemplateCharacterChange(null);
    } else {
      onSelectedTemplateCharacterChange(isSelected ? null : character._id);
      onSelectedCustomCharacterChange(null);
    }
  };



  const displayedCharacters = allCharacters; // Server now filters, so allow all
  const characterCounts = {
    all: allCharacters.length,
    studio: allCharacters.filter((character) => character.visibility === "public").length,
    custom: allCharacters.filter((character) => character.visibility === "private").length,
  };
  const hasActiveFilters = Object.values(selectedFilters).some(
    (arr) => arr.length > 0
  );

  const getCharacters = async (params = {}) => {
    await services.getCharacters(params);
  };

  // Build params and fetch characters when filters change
  useEffect(() => {
    const params = {};

    // Type filter from View Filter (Top Tabs)
    if (viewFilter === "all") params.type = "all";
    if (viewFilter === "studio") params.type = "public";
    if (viewFilter === "custom") params.type = "private";
    // Favorites is handled locally or separate API if needed, 
    // but for now keeping it client-side if "favorites" is selected, 
    // or if the user meant "favorites" tab should just filter favorites from "all"
    // User instructions: "For the all for all tabs, public value for the studio character and private for the custom characters."
    // I will fetch 'all' for favorites and filter client side, or if API supports type=favorites? 
    // User didn't specify favorites param key. 
    // Best approach: If viewFilter is 'favorites', maybe fetch 'all' and filter client side?
    // OR just fetch 'all' and filter by favorites locally.
    // However, server filtering replaces `allCharacters`.
    // Let's assume for favorites we fetch 'all' and filter client side, 
    // OR if viewFilter is favorites, we don't send type param (defaults to all?) or send type=all.

    if (viewFilter === "favorites") params.type = "all";

    // Sidebar Filters
    if (selectedFilters.gender.length > 0) {
      params.gender = selectedFilters.gender.join(",");
    }
    if (selectedFilters.ageCategory.length > 0) {
      params.ageCategory = selectedFilters.ageCategory.join(",");
    }

    // Trigger API
    getCharacters(params);

  }, [viewFilter, selectedFilters]);

  // Client-side search and favorites filtering (applied on top of server results)
  const getProcessedCharacters = () => {
    let chars = displayedCharacters;

    // Apply favorites filter locally if tab selected
    if (viewFilter === "favorites") {
      chars = chars.filter((c) => favoritedCharacters.includes(c._id));
    }

    // Apply search filter locally (or move to server if needed, sticking to client for now as per plan focus)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      chars = chars.filter((character) => {
        return (
          character.name?.toLowerCase().includes(query) ||
          character.description?.toLowerCase().includes(query) ||
          (character.attributes?.age?.toString() || "").includes(query)
        );
      });
    }

    return chars;
  }

  const finalCharacters = getProcessedCharacters();

  // Load favorites from localStorage on mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem("favoritedCharacters");
    if (savedFavorites) {
      setFavoritedCharacters(JSON.parse(savedFavorites));
    }
  }, []);

  // Save favorites to localStorage when they change
  useEffect(() => {
    localStorage.setItem(
      "favoritedCharacters",
      JSON.stringify(favoritedCharacters)
    );
  }, [favoritedCharacters]);

  return (
    <div className="character-library-layout flex gap-6">
      <button
        type="button"
        className="character-mobile-filter-toggle"
        onClick={() => setMobileFiltersOpen((open) => !open)}
        aria-controls="character-library-filters"
        aria-expanded={mobileFiltersOpen}
      >
        <SlidersHorizontal className="h-4 w-4" />
        Filters{hasActiveFilters ? ` (${Object.values(selectedFilters).reduce((total, values) => total + values.length, 0)})` : ""}
        <ChevronDown className={`h-4 w-4 transition-transform ${mobileFiltersOpen ? "rotate-180" : ""}`} />
      </button>
      {/* Filters Sidebar */}
      <aside id="character-library-filters" className={`character-library-filters w-52 flex-shrink-0 ${mobileFiltersOpen ? "is-open" : ""}`}>
        <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#111111] p-4 sticky top-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Filters
            </h3>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="h-7 px-2 text-xs rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5"
              >
                Clear all
              </button>
            )}
          </div>

          <div className="space-y-2">
            {/* Gender Filter */}
            <div>
              <button
                onClick={() => toggleFilterSection("gender")}
                className="w-full flex items-center justify-between py-2 px-2 rounded-lg transition-colors hover:bg-gray-50 dark:hover:bg-white/5"
              >
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Gender
                </span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${filterSectionsOpen.gender ? "rotate-0" : "-rotate-90"
                    } text-gray-500 dark:text-gray-400`}
                />
              </button>
              {filterSectionsOpen.gender && (
                <div className="mt-2 space-y-1.5 pl-2">
                  {filterOptions.gender.map((option) => (
                    <button
                      key={option}
                      onClick={() => toggleFilter("gender", option)}
                      className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-all ${selectedFilters.gender.includes(option)
                        ? "bg-orange-500 text-white font-medium"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5"
                        }`}
                    >
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Age Category Filter */}
            <div>
              <button
                onClick={() => toggleFilterSection("ageCategory")}
                className="w-full flex items-center justify-between py-2 px-2 rounded-lg transition-colors hover:bg-gray-50 dark:hover:bg-white/5"
              >
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Age Category
                </span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${filterSectionsOpen.ageCategory ? "rotate-0" : "-rotate-90"
                    } text-gray-500 dark:text-gray-400`}
                />
              </button>
              {filterSectionsOpen.ageCategory && (
                <div className="mt-2 space-y-1.5 pl-2">
                  {filterOptions.ageCategory.map((option) => (
                    <button
                      key={option}
                      onClick={() => toggleFilter("ageCategory", option)}
                      className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-all ${selectedFilters.ageCategory.includes(option)
                        ? "bg-orange-500 text-white font-medium"
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5"
                        }`}
                    >
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Column */}
      <div className="character-library-results flex-1 min-w-0">
        <div className="character-library-view-tabs flex items-center gap-2 mb-6" role="group" aria-label="Character type">
          <button
            onClick={() => setViewFilter("all")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewFilter === "all"
              ? "bg-orange-500 text-white"
              : "bg-white dark:bg-[#1a1a1a] text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#222222] border border-gray-200 dark:border-white/5"
              }`}
          >
            All ({characterCounts.all})
          </button>
          <button
            onClick={() => setViewFilter("studio")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewFilter === "studio"
              ? "bg-orange-500 text-white"
              : "bg-white dark:bg-[#1a1a1a] text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#222222] border border-gray-200 dark:border-white/5"
              }`}
          >
            Studio Characters ({characterCounts.studio})
          </button>
          <button
            onClick={() => setViewFilter("custom")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewFilter === "custom"
              ? "bg-orange-500 text-white"
              : "bg-white dark:bg-[#1a1a1a] text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#222222] border border-gray-200 dark:border-white/5"
              }`}
          >
            Custom Characters ({characterCounts.custom})
          </button>
          <button
            onClick={() => setViewFilter("favorites")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewFilter === "favorites"
              ? "bg-orange-500 text-white"
              : "bg-white dark:bg-[#1a1a1a] text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#222222] border border-gray-200 dark:border-white/5"
              }`}
          >
            Favorites
          </button>
        </div>

        {/* Selection Header */}
        {selectedItems.length > 0 && (
          <div className="sticky top-0 z-50 flex items-center justify-between bg-blue-50/90 dark:bg-blue-900/40 backdrop-blur-md border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6 transition-all animate-in fade-in slide-in-from-top-2 shadow-lg shadow-blue-500/10">
            <div className="flex items-center gap-3">
              <span className="text-blue-800 dark:text-blue-200 font-medium">
                {selectedItems.length} character{selectedItems.length !== 1 ? "s" : ""} selected
              </span>
              <button
                onClick={clearSelection}
                className="text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30 px-3 py-1 rounded-lg text-sm flex items-center gap-1 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Clear selection
              </button>
            </div>
            <button
              onClick={handleDeleteAction}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 shadow-sm transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete selected
            </button>
          </div>
        )}

        {loading ? (
          <Loading />
        ) : (
          <CharacterGrid
            characters={finalCharacters}
            selectedCustomCharacter={selectedCustomCharacter}
            selectedTemplateCharacter={selectedTemplateCharacter}
            selectedItems={selectedItems}
            favoritedCharacters={favoritedCharacters}
            onCharacterClick={handleCharacterClick}
            onToggleFavorite={toggleFavorite}
            onToggleSelection={toggleSelection}
            showDelete={viewFilter === "custom" || viewFilter === "all"}
          />
        )}
      </div>

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Characters"
        message={`Are you sure you want to delete ${selectedItems.length === 1 ? "this character" : `these ${selectedItems.length} characters`}? This action cannot be undone.`}
        confirmLabel={isDeleting ? <div className="flex justify-center"><CircularLoader size={20} /></div> : "Delete"}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />
    </div>
  );
}

function CharacterGrid({
  characters,
  selectedCustomCharacter,
  selectedTemplateCharacter,
  selectedItems,
  favoritedCharacters,
  onCharacterClick,
  onToggleFavorite,
  onToggleSelection,
  showDelete = false,
}) {
  if (characters.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400 dark:text-gray-500">
        <i
          data-lucide="user-circle"
          className="h-16 w-16 mx-auto mb-4 opacity-50"
        ></i>
        <p className="text-lg font-medium mb-2">No characters found</p>
        <p className="text-sm">Try adjusting your filters or search query</p>
      </div>
    );
  }

  return (
    <div className="character-library-grid grid grid-cols-8 gap-4">
      {characters?.map((character) => {
        const isCustom =
          character.type === "ai_generated" || character.type === "custom";
        const isSelected = isCustom
          ? selectedCustomCharacter === character._id
          : selectedTemplateCharacter === character._id;
        const isFavorited = favoritedCharacters.includes(character._id);
        const imageUrl =
          character.baseImage?.originalUrl ||
          character.metadata?.baseImageUrl ||
          "/placeholder.svg";

        return (
          <div
            key={character._id}
            className={`group rounded-xl border overflow-hidden cursor-pointer transition-all hover:scale-105 hover:z-10 relative ${isSelected
              ? "ring-2 ring-orange-500 border-orange-500 shadow-xl dark:shadow-2xl dark:shadow-orange-500/30"
              : "border-gray-200 dark:border-white/10 bg-white dark:bg-[#111111] hover:border-orange-300 dark:hover:border-orange-500/50 hover:shadow-xl dark:hover:shadow-2xl dark:hover:shadow-orange-500/20"
              }`}
          >
            <div className="relative aspect-[9/16] overflow-hidden">
              <button
                type="button"
                onClick={() => onCharacterClick(character)}
                className="absolute inset-0 z-[1] w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-orange-500"
                aria-label={`Select ${character.name}`}
                aria-pressed={isSelected}
              />
              <img
                src={imageUrl}
                alt={character.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
              {isSelected && (
                <div className="absolute top-2 left-2 w-7 h-7 rounded-lg flex items-center justify-center z-10">
                  <i
                    data-lucide="check"
                    className="h-4 w-4 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
                  ></i>
                </div>
              )}

              <button
                onClick={(e) => onToggleFavorite(character._id, e)}
                aria-label={`${isFavorited ? "Remove" : "Add"} ${character.name} ${isFavorited ? "from" : "to"} favorites`}
                aria-pressed={isFavorited}
                className={`absolute top-2 right-2 z-10 w-7 h-7 rounded-lg flex items-center justify-center transition-all backdrop-blur-sm ${isFavorited
                  ? "bg-orange-500 text-white opacity-100"
                  : "bg-black/40 dark:bg-white/80 text-white dark:text-gray-700 hover:bg-black/60 dark:hover:bg-white opacity-0 group-hover:opacity-100"
                  }`}
              >
                <Heart
                  className={`h-3 w-3 ${isFavorited ? "fill-current" : ""}`}
                />
              </button>

              {/* Selection Checkbox */}
              {showDelete && character.visibility === "private" && (
                <button
                  type="button"
                  aria-label={`${selectedItems.includes(character._id) ? "Deselect" : "Select"} ${character.name} for deletion`}
                  aria-pressed={selectedItems.includes(character._id)}
                  className={`absolute top-2 left-2 z-10 selection-checkbox ${selectedItems.includes(character._id) || "group-hover:opacity-100 opacity-0"
                    } transition-opacity`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelection(character._id);
                  }}
                >
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${selectedItems.includes(character._id)
                      ? "bg-blue-600 border-blue-600"
                      : "bg-white/80 dark:bg-black/40 border-gray-300 dark:border-gray-600 hover:border-blue-400 backdrop-blur-sm"
                      }`}
                  >
                    {selectedItems.includes(character._id) && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </button>
              )}


              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
                {character.visibility === "private" && (
                  <div className="absolute bottom-8 left-2 z-10">
                    <div className="px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm flex items-center">
                      <span className="text-[10px] font-semibold text-white uppercase tracking-wide leading-none">
                        Custom
                      </span>
                    </div>
                  </div>
                )}
                <h4 className="text-sm font-semibold truncate text-white">
                  {character.name}
                </h4>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
